/**
 * Dashboard Assistant Panel (New - Using Shared Components)
 * 
 * Embedded AI assistant for the Expert Dashboard using shared component architecture.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useAuth0 } from '@auth0/auth0-react';
import { useDashboardAssistant } from './DashboardAssistantContext';
import { getDashboardContextualHelp } from './dashboardKnowledge';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useToast } from '../../Context/ToastContext';

// Import shared styled components
import {
  EmbeddedPanelContainer,
  PanelHeader,
  HeaderTitle,
  HeaderIcon,
  TitleText,
  HeaderActions,
  IconButton,
  MessagesContainer,
  MessageGroup,
  MessageBubble,
  TypingIndicator,
  SuggestionsContainer,
  SuggestionButton,
  InputContainer,
  InputWrapper,
  TextInput,
  SendButton,
  MicButton,
  RecordingDot,
  RecordingTimer,
  TranscribingIndicator,
  WelcomeMessage,
} from '../SharedAssistant/AssistantStyles';

// Import SVG icon
import HubAssistantIcon from '../../Images/hubAssistant.svg';

/**
 * Parse markdown text to HTML
 */
const parseMarkdown = (text) => {
  if (!text) return '';
  
  const paragraphs = text.split(/\n\n+/);
  
  const parsedParagraphs = paragraphs.map(paragraph => {
    let p = paragraph.trim();
    if (!p) return '';
    
    if (/^[-*_]{3,}$/.test(p)) return '<hr class="md-hr"/>';
    if (p.startsWith('### ')) return `<div class="md-h3">${p.slice(4)}</div>`;
    if (p.startsWith('## ')) return `<div class="md-h2">${p.slice(3)}</div>`;
    if (p.startsWith('# ')) return `<div class="md-h1">${p.slice(2)}</div>`;
    
    const lines = p.split('\n');
    const isNumberedList = lines.every(line => /^\d+[\.\)]\s/.test(line.trim()) || line.trim() === '');
    const isBulletList = lines.every(line => /^[-•]\s/.test(line.trim()) || line.trim() === '');
    
    if (isNumberedList && lines.some(line => /^\d+[\.\)]\s/.test(line.trim()))) {
      const items = lines.filter(line => line.trim())
        .map(line => `<li>${line.replace(/^\d+[\.\)]\s*/, '').trim().replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</li>`)
        .join('');
      return `<ol>${items}</ol>`;
    }
    
    if (isBulletList && lines.some(line => /^[-•]\s/.test(line.trim()))) {
      const items = lines.filter(line => line.trim())
        .map(line => `<li>${line.replace(/^[-•]\s*/, '').trim().replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</li>`)
        .join('');
      return `<ul>${items}</ul>`;
    }
    
    return `<p>${p.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/`(.*?)`/g, '<code>$1</code>').replace(/\n/g, '<br/>')}</p>`;
  });
  
  return parsedParagraphs.filter(p => p).join('');
};

const DashboardAssistantPanelNew = () => {
  const authenticatedAxios = useAuthenticatedAxios();
  const toast = useToast();
  const showToast = toast?.showToast || (() => {});
  const { user } = useAuth0();
  
  const {
    conversationHistory,
    addMessage,
    clearConversation,
    isLoading,
    setIsLoading,
    getFullContext,
  } = useDashboardAssistant();

  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Speech-to-text state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingMimeType, setRecordingMimeType] = useState('audio/webm');
  const timerRef = useRef(null);

  const contextualHelp = getDashboardContextualHelp();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const toggleRecording = async () => {
    if (isRecording) {
      if (mediaRecorder) {
        mediaRecorder.stop();
        clearInterval(timerRef.current);
        setRecordingTime(0);
      }
      setIsRecording(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true }
        });

        let mimeType = 'audio/webm';
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mimeType = 'audio/webm;codecs=opus';
        else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) mimeType = 'audio/ogg;codecs=opus';

        setRecordingMimeType(mimeType);
        const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 });
        const currentChunks = [];

        recorder.ondataavailable = (e) => { if (e.data.size > 0) currentChunks.push(e.data); };
        recorder.onstop = () => transcribeAudio(stream, currentChunks);

        recorder.start(1000);
        setMediaRecorder(recorder);
        setIsRecording(true);
        timerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
      } catch (error) {
        showToast('Could not access microphone.', 'error');
      }
    }
  };

  const transcribeAudio = async (stream, chunks) => {
    try {
      stream.getTracks().forEach(track => track.stop());
      setIsTranscribing(true);

      const audioBlob = new Blob(chunks, { type: recordingMimeType });
      const ext = recordingMimeType.includes('webm') ? 'webm' : 'ogg';
      
      const formData = new FormData();
      formData.append('file', audioBlob, `recording_${Date.now()}.${ext}`);

      const response = await authenticatedAxios.post('/api/process_audio', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'X-Expert-ID': user?.sub || '', // Required for Whisper billing
        },
        timeout: 60000
      });

      if (response.data?.transcription?.trim()) {
        setInput(prev => prev + (prev.length > 0 ? ' ' : '') + response.data.transcription);
      }
    } catch (error) {
      showToast('Error transcribing audio.', 'error');
    } finally {
      setIsTranscribing(false);
    }
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const handleSendMessage = useCallback(async (messageText = null) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    addMessage({ role: 'user', content: text });
    setInput('');
    setShowSuggestions(false);
    setIsLoading(true);

    try {
      const fullContext = getFullContext();
      
      const response = await authenticatedAxios.post('/dashboard/assistant/message', {
        message: text,
        context: fullContext,
        conversationHistory: conversationHistory.slice(-10)
      });

      addMessage({
        role: 'assistant',
        content: response.data.message || "I couldn't process that request."
      });
    } catch (error) {
      addMessage({ role: 'assistant', content: "I'm having trouble connecting. Please try again." });
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, addMessage, setIsLoading, getFullContext, authenticatedAxios, conversationHistory]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  return (
    <EmbeddedPanelContainer>
      <PanelHeader>
        <HeaderTitle>
          <HeaderIcon><img src={HubAssistantIcon} alt="" /></HeaderIcon>
          <TitleText>
            <h3>Dashboard Assistant</h3>
            <span>Help with your metrics</span>
          </TitleText>
        </HeaderTitle>
        <HeaderActions>
          <IconButton onClick={clearConversation} title="Clear">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"/>
            </svg>
          </IconButton>
        </HeaderActions>
      </PanelHeader>

      <MessagesContainer>
        {conversationHistory.length === 0 ? (
          <>
            <WelcomeMessage>
              <div className="icon-wrapper"><img src={HubAssistantIcon} alt="" /></div>
              <h4>Dashboard Assistant</h4>
              <p>I can help you understand all your dashboard metrics, case statistics, and performance data.</p>
            </WelcomeMessage>
            
            {showSuggestions && contextualHelp.suggestions?.length > 0 && (
              <SuggestionsContainer>
                {contextualHelp.suggestions.slice(0, 4).map((suggestion, idx) => (
                  <SuggestionButton key={idx} onClick={() => handleSendMessage(suggestion)}>
                    {suggestion}
                  </SuggestionButton>
                ))}
              </SuggestionsContainer>
            )}
          </>
        ) : (
          <>
            {conversationHistory.map((msg, idx) => (
              <MessageGroup key={idx}>
                <MessageBubble 
                  $isUser={msg.role === 'user'}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.role === 'user' ? msg.content : parseMarkdown(msg.content)) }}
                />
              </MessageGroup>
            ))}
            {isLoading && <TypingIndicator><span /><span /><span /></TypingIndicator>}
          </>
        )}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer>
        {isTranscribing && (
          <TranscribingIndicator>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4"/>
            </svg>
            Transcribing...
          </TranscribingIndicator>
        )}
        <InputWrapper>
          <TextInput
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={isRecording ? "Recording..." : "Ask about your dashboard..."}
            disabled={isLoading || isRecording}
            rows={1}
          />
          <MicButton onClick={toggleRecording} disabled={isLoading || isTranscribing} $isRecording={isRecording}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a4 4 0 0 1 4 4v4a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/>
            </svg>
            {isRecording && <><RecordingDot /><RecordingTimer>{formatTime(recordingTime)}</RecordingTimer></>}
          </MicButton>
          <SendButton onClick={() => handleSendMessage()} disabled={!input.trim() || isLoading || isRecording}>↑</SendButton>
        </InputWrapper>
      </InputContainer>
    </EmbeddedPanelContainer>
  );
};

export default DashboardAssistantPanelNew;

